import { localId } from './ids.js';
function norm(s) {
    return s.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
}
function distribute(tokens, startMs, endMs) {
    if (tokens.length === 0)
        return [];
    const span = Math.max(1, endMs - startMs);
    const lens = tokens.map((t) => Math.max(1, [...t].length));
    const total = lens.reduce((a, b) => a + b, 0);
    let cursor = startMs;
    return tokens.map((_, i) => {
        const frac = lens[i] / total;
        const dur = Math.max(1, Math.round(span * frac));
        const s = cursor;
        const e = i === tokens.length - 1 ? endMs : Math.min(endMs - (tokens.length - 1 - i), s + dur);
        cursor = Math.max(e, s + 1);
        return { startMs: s, endMs: Math.max(s + 1, e) };
    });
}
/**
 * Given a segment's current words (in order) and new sentence text, produce a
 * new list of timed words covering the same [start, end] window.
 */
export function resplitSegment(oldWords, newText) {
    const tokens = newText.trim().split(/\s+/).filter(Boolean);
    if (oldWords.length === 0)
        return oldWords;
    if (tokens.length === 0)
        return []; // caller may drop the now-empty segment
    const segStart = oldWords[0].startMs;
    const segEnd = oldWords[oldWords.length - 1].endMs;
    // Longest common prefix (by normalized text).
    let pre = 0;
    while (pre < tokens.length &&
        pre < oldWords.length &&
        norm(tokens[pre]) === norm(oldWords[pre].text)) {
        pre++;
    }
    // Longest common suffix, not overlapping the prefix.
    let suf = 0;
    while (suf < tokens.length - pre &&
        suf < oldWords.length - pre &&
        norm(tokens[tokens.length - 1 - suf]) === norm(oldWords[oldWords.length - 1 - suf].text)) {
        suf++;
    }
    const prefixWords = [];
    for (let i = 0; i < pre; i++) {
        // Keep original timing + id; adopt the user's new text (capitalization/punct).
        prefixWords.push({ ...oldWords[i], text: tokens[i] });
    }
    const suffixWords = [];
    for (let i = 0; i < suf; i++) {
        const oldIdx = oldWords.length - suf + i;
        suffixWords.push({ ...oldWords[oldIdx], text: tokens[tokens.length - suf + i] });
    }
    const midTokens = tokens.slice(pre, tokens.length - suf);
    const midStart = prefixWords.length ? prefixWords[prefixWords.length - 1].endMs : segStart;
    const midEnd = suffixWords.length ? suffixWords[0].startMs : segEnd;
    const midWords = distribute(midTokens, midStart, Math.max(midStart + 1, midEnd)).map((t, i) => ({ id: localId('w'), text: midTokens[i], startMs: t.startMs, endMs: t.endMs }));
    return [...prefixWords, ...midWords, ...suffixWords];
}
/** Join a segment's words back into an editable sentence. */
export function segmentText(words) {
    return words.map((w) => w.text).join(' ');
}
/**
 * Format milliseconds as m:ss.mmm (or h:mm:ss.mmm) for the transcript
 * timestamps. Captions are inherently sub-second, so we always show the
 * millisecond part - otherwise two fast lines can look identical (e.g. 2000ms
 * and 2800ms both rendering as "0:02"). parseTimestamp round-trips this.
 */
export function formatTimestamp(ms) {
    const clamped = Math.max(0, Math.round(ms));
    const totalS = Math.floor(clamped / 1000);
    const msPart = clamped % 1000;
    const s = totalS % 60;
    const m = Math.floor(totalS / 60) % 60;
    const h = Math.floor(totalS / 3600);
    const pad = (n) => String(n).padStart(2, '0');
    const base = h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
    return `${base}.${String(msPart).padStart(3, '0')}`;
}
/**
 * Parse a user-typed timestamp back to milliseconds. Accepts `ss`, `m:ss`,
 * `mm:ss`, `h:mm:ss`, each optionally with a decimal `.ms` (e.g. `1:23.5`).
 * Returns null if it can't be parsed.
 */
export function parseTimestamp(input) {
    const s = input.trim();
    if (!s)
        return null;
    const parts = s.split(':');
    if (parts.length < 1 || parts.length > 3)
        return null;
    const nums = [];
    for (const p of parts) {
        if (!/^\d+(\.\d+)?$/.test(p))
            return null;
        nums.push(parseFloat(p));
    }
    let seconds;
    if (nums.length === 1)
        seconds = nums[0];
    else if (nums.length === 2)
        seconds = nums[0] * 60 + nums[1];
    else
        seconds = nums[0] * 3600 + nums[1] * 60 + nums[2];
    if (!Number.isFinite(seconds) || seconds < 0)
        return null;
    return Math.round(seconds * 1000);
}
//# sourceMappingURL=resplit.js.map